// ===== HORIZONTAL SCROLL FOR MULTI-COLUMN SECTIONS =====
// Added functionality for horizontal scrolling when desktop has 3 columns per row but 4+ total columns
// This creates a mobile-like horizontal scroll experience on desktop

document.addEventListener("DOMContentLoaded", function () {
  // Find all multi-column sections with horizontal scroll enabled
  const horizontalScrollSections = document.querySelectorAll(
    'multi-column-v2[data-horizontal-scroll="true"]'
  );

  horizontalScrollSections.forEach(function (section) {
    // Only initialize horizontal scroll functionality on desktop
    if (window.innerWidth < 999) {
      return;
    }

    const sectionId = section.getAttribute("data-section-id");

    // Add smooth scrolling behavior
    section.style.scrollBehavior = "smooth";

    // Ensure consistent image heights
    normalizeImageHeights(section);

    // Ensure consistent column widths
    normalizeColumnWidths(section);

    // Optional: Add keyboard navigation (arrow keys)
    section.addEventListener("keydown", function (e) {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        // Calculate scroll amount based on viewport for edge-to-edge
        const viewportWidth = window.innerWidth;
        const sectionPadding = 1.5 * 16 * 2; // 1.5rem * 16px * 2 sides
        const availableWidth = viewportWidth - sectionPadding;
        const columnsPerScroll = getColumnsPerScroll();
        const scrollAmount = availableWidth / columnsPerScroll; // Scroll by one column width
        section.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        // Calculate scroll amount based on viewport for edge-to-edge
        const viewportWidth = window.innerWidth;
        const sectionPadding = 1.5 * 16 * 2; // 1.5rem * 16px * 2 sides
        const availableWidth = viewportWidth - sectionPadding;
        const columnsPerScroll = getColumnsPerScroll();
        const scrollAmount = availableWidth / columnsPerScroll; // Scroll by one column width
        section.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    });

    // Make section focusable for keyboard navigation
    section.setAttribute("tabindex", "0");

    // Optional: Add scroll indicators (dots or progress bar)
    addScrollIndicators(section, sectionId);

    // Re-normalize heights and widths on window resize
    const resizeObserver = new ResizeObserver(() => {
      normalizeColumnWidths(section);
      normalizeImageHeights(section);
    });
    resizeObserver.observe(section);
  });

  // Function to normalize column widths for consistent appearance
  function normalizeColumnWidths(section) {
    // Only apply column width normalization on desktop (999px+)
    if (window.innerWidth < 999) {
      // Reset any previously applied styles for mobile
      const items = section.querySelectorAll(".multi-column__item");
      items.forEach((item) => {
        item.style.width = "";
        item.style.minWidth = "";
        item.style.maxWidth = "";
        item.style.flexBasis = "";
      });
      return;
    }

    const items = section.querySelectorAll(".multi-column__item");
    // For edge-to-edge scroll, use full viewport width minus padding
    const viewportWidth = window.innerWidth;
    const sectionPadding = 1.5 * 16 * 2; // 1.5rem * 16px * 2 sides = 48px
    const gap = parseFloat(getComputedStyle(section).gap) || 0;

    // Calculate available width for columns
    const availableWidth = viewportWidth - sectionPadding;

    // Calculate the exact width for each column
    const isWideViewport = window.innerWidth >= 1800;
    const columnsVisible = isWideViewport ? 4 : 3.2; // 4 columns on very wide screens, 3 + peek otherwise
    const gapsCount = isWideViewport ? 3 : 2;
    const columnWidth = (availableWidth - gap * gapsCount) / columnsVisible;

    items.forEach((item) => {
      item.style.width = columnWidth + "px";
      item.style.minWidth = columnWidth + "px";
      item.style.maxWidth = columnWidth + "px";
      item.style.flexBasis = columnWidth + "px";
    });
  }

  // Function to normalize image heights for consistent appearance
  function normalizeImageHeights(section) {
    const images = section.querySelectorAll(".multi-column__item img");

    // Only apply image height normalization on desktop (999px+)
    if (window.innerWidth < 999) {
      // Reset any previously applied styles for mobile
      images.forEach((img) => {
        img.style.height = "";
        img.style.objectFit = "";
      });
      return;
    }

    // Wait for all images to load before calculating heights
    const imageLoadPromises = Array.from(images).map((img) => {
      return new Promise((resolve) => {
        if (img.complete) {
          resolve();
        } else {
          img.addEventListener("load", resolve);
          img.addEventListener("error", resolve); // Also resolve on error to avoid hanging
        }
      });
    });

    Promise.all(imageLoadPromises).then(() => {
      // Find the maximum natural height among all images
      let maxHeight = 0;
      images.forEach((img) => {
        const aspectRatio = img.naturalWidth / img.naturalHeight;
        const containerWidth = img.offsetWidth;
        const calculatedHeight = containerWidth / aspectRatio;
        maxHeight = Math.max(maxHeight, calculatedHeight);
      });

      // Apply the consistent height to all images
      if (maxHeight > 0) {
        images.forEach((img) => {
          img.style.height = maxHeight + "px";
          img.style.objectFit = "cover";
        });
      }
    });
  }

  // Function to add scroll indicators
  function addScrollIndicators(section, sectionId) {
    const items = section.querySelectorAll(".multi-column__item");
    const totalItems = items.length;

    if (totalItems <= 3) return; // No need for indicators if 3 or fewer items

    // Create indicator container
    const indicatorContainer = document.createElement("div");
    indicatorContainer.className = "horizontal-scroll-indicators";
    indicatorContainer.style.cssText = `
      display: flex;
      justify-content: center;
      gap: 8px;
      margin-top: 1rem;
      padding: 0;
    `;

    // Calculate number of "pages" (sets of 3 columns)
    const columnsPerScroll = getColumnsPerScroll();
    const totalPages = Math.max(Math.ceil(totalItems / columnsPerScroll), 1);

    // Create indicator dots
    for (let i = 0; i < totalPages; i++) {
      const dot = document.createElement("button");
      dot.className = "scroll-indicator-dot";
      dot.style.cssText = `
        width: 8px;
        height: 8px;
        border-radius: 50%;
        border: none;
        background-color: rgba(0,0,0,0.2);
        cursor: pointer;
        transition: background-color 0.3s ease;
        padding: 0;
        margin: 0;
      `;

      // Add click handler to scroll to specific position
      dot.addEventListener("click", function () {
        // Calculate scroll position based on viewport width for edge-to-edge
        const viewportWidth = window.innerWidth;
        const sectionPadding = 1.5 * 16 * 2; // 1.5rem * 16px * 2 sides
        const availableWidth = viewportWidth - sectionPadding;
        const scrollPosition = i * (availableWidth / columnsPerScroll);
        section.scrollTo({ left: scrollPosition, behavior: "smooth" });
      });

      indicatorContainer.appendChild(dot);
    }

    // Insert indicators after the section
    section.parentNode.insertBefore(indicatorContainer, section.nextSibling);

    // Update active indicator on scroll
    section.addEventListener("scroll", function () {
      const scrollLeft = section.scrollLeft;
      // Calculate column width based on viewport for edge-to-edge
      const viewportWidth = window.innerWidth;
      const sectionPadding = 1.5 * 16 * 2; // 1.5rem * 16px * 2 sides
      const availableWidth = viewportWidth - sectionPadding;
      const columnsPerScroll = getColumnsPerScroll();
      const columnWidth = availableWidth / columnsPerScroll;
      const activeIndex = Math.round(scrollLeft / columnWidth);

      const dots = indicatorContainer.querySelectorAll(".scroll-indicator-dot");
      dots.forEach(function (dot, index) {
        if (index === activeIndex) {
          dot.style.backgroundColor = "rgba(0,0,0,0.8)";
        } else {
          dot.style.backgroundColor = "rgba(0,0,0,0.2)";
        }

        function getColumnsPerScroll() {
          return window.innerWidth >= 1800 ? 4 : 3;
        }
      });
    });

    // Set initial active state
    const firstDot = indicatorContainer.querySelector(".scroll-indicator-dot");
    if (firstDot) {
      firstDot.style.backgroundColor = "rgba(0,0,0,0.8)";
    }
  }
});

// Optional: Add touch/swipe support for better mobile compatibility
// This ensures smooth interaction across all devices
document.addEventListener("DOMContentLoaded", function () {
  const horizontalScrollSections = document.querySelectorAll(
    'multi-column-v2[data-horizontal-scroll="true"]'
  );

  horizontalScrollSections.forEach(function (section) {
    // Only apply mouse drag functionality on desktop
    if (window.innerWidth < 999) {
      return;
    }
    let startX = 0;
    let startScrollLeft = 0;
    let isDragging = false;

    section.addEventListener("mousedown", function (e) {
      isDragging = true;
      startX = e.pageX;
      startScrollLeft = section.scrollLeft;
      section.style.cursor = "grabbing";
      section.style.userSelect = "none";
    });

    section.addEventListener("mouseup", function () {
      isDragging = false;
      section.style.cursor = "grab";
      section.style.userSelect = "";
    });

    section.addEventListener("mouseleave", function () {
      isDragging = false;
      section.style.cursor = "grab";
      section.style.userSelect = "";
    });

    section.addEventListener("mousemove", function (e) {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX;
      const walkX = (x - startX) * 2; // Multiply by 2 for faster scrolling
      section.scrollLeft = startScrollLeft - walkX;
    });

    // Set initial cursor
    section.style.cursor = "grab";
  });
});
// ===== END HORIZONTAL SCROLL FOR MULTI-COLUMN SECTIONS =====
